import stemmer from "stemmer";
import getReadyScoresForRun from "../score_passes/score_pass_1";
import getFrustrationScoreForRun from "../score_passes/score_pass_2";
import selectWord from "../intervene_selector";
import getConfigForLocale from "../config/getConfigForLocale";
import { embeddedInterventionTypes } from "../constants";

export function wordsMatch(word1, word2) {
  return (
    word1.replace("'", "").replace(",", "").toUpperCase() ==
    word2.replace("'", "").replace(",", "").toUpperCase()
  );
}

// Return metadata for a word in the current phrase
export function getWordMetadata(chosenWord, state, wordIndex) {
  if (!chosenWord) {
    return;
  }

  let metadataIndex = wordIndex !== undefined ? wordIndex : state.wordIndex;
  let words = state.phraseWordMetadata;

  if (metadataIndex !== undefined) {
    const metadata = words[metadataIndex];
    if (metadata && metadata.WORD && wordsMatch(metadata.WORD, chosenWord)) {
      return metadata;
    }
  }

  // We could not find appropriate metadata by index, fall back to searching by word string match
  return words.find((word) => {
    if (!word || !word.WORD) {
      return;
    }

    return wordsMatch(word.WORD, chosenWord);
  });
}

// Set corresponding word metadata fields for a word that we'll intervene on
// Note: these fields are only generated for word level interventions
export function createSelectedWordFields(wordToInterveneOn, state, wordIndex) {
  if (!wordToInterveneOn) {
    return;
  }

  const wordFields = getWordMetadata(wordToInterveneOn, state, wordIndex);

  if (wordFields) {
    Object.keys(wordFields).forEach((key) => {
      if (key != "WORD") {
        state["word_" + key] = wordFields[key];
      }
    });
  }

  // Add closeError data if it exists in closeErrors array
  setCloseErrorData(wordToInterveneOn, state, wordIndex);

  // Add metadata for each embedded intervention
  setEmbeddedInterventionMetadata(state);

  // Set multisyllable intervention metadata
  setMsylMetadata(state);
}

function setCloseErrorData(word, state, wordIndex) {
  const closeErrors = state.closeErrors;
  if (!closeErrors || closeErrors.length === 0) {
    return;
  }

  let closeError;
  const errorIndex = wordIndex !== undefined ? wordIndex : state.wordIndex;

  closeError = closeErrors.find((error) => {
    return error.index === errorIndex;
  });

  if (!closeError || !wordsMatch(closeError.desiredWord, word)) {
    closeError = closeErrors.find((error) => {
      return wordsMatch(error.desiredWord, word);
    });
  }

  if (closeError) {
    state.closeError = closeError;

    if (stemmer(closeError.desiredWord) === stemmer(closeError.utteredWord)) {
      state.tenseError = closeError;
    }
  }
}

/**
 * Copies embedded intervention metadata in the given state object to the root level of the state object.
 * Used by the intervention selector to check for required metadata when setting intervention validity.
 *
 * @param {Object} state - The state object containing the intervention metadata.
 */
function setEmbeddedInterventionMetadata(state) {
  // Retrieve the metadata related to embedded interventions from the state
  const embeddedInterventionMetadata = state.word_EMBEDDED_INTERVENTIONS;

  // Exit early if no metadata is found or if the metadata is an empty array
  if (
    !embeddedInterventionMetadata ||
    embeddedInterventionMetadata.length === 0
  ) {
    return;
  }

  // Get an array of intervention types from the predefined embedded intervention types object
  const embeddedInterventions = Object.values(embeddedInterventionTypes);

  // Loop through each intervention type and find matching metadata in the state
  for (const interventionType of embeddedInterventions) {
    const match = embeddedInterventionMetadata.find((metadata) => {
      // Check if the current metadata corresponds to the current intervention type
      return metadata.interventionType === interventionType;
    });

    // If a match is found, update the state with a new root level accessor with the corresponding metadata
    if (match) {
      state["word_" + interventionType] = match;
    }
  }
}

/**
 * Updates the state object with metadata related to multisyllable intervention validity.
 *
 * @param {Object} state - The state object containing the intervention metadata.
 */
function setMsylMetadata(state) {
  // Retrieve the metadata related to multisyllable intervention from the state
  const syllableEncoding = state.word_SYLLABLE_ENCODING;

  // Exit early if no metadata is found or if the metadata is an empty string
  if (!syllableEncoding || syllableEncoding === "") {
    return;
  }

  // Split the string on the ':' character
  const parts = syllableEncoding.split(":");

  // Check if there is a split code
  if (parts[0] && parts[0] !== "") {
    state.word_MSYL_SPLIT_CODE = parts[0];
  }

  // Check the syllables part, which is after the ':'
  if (parts.length > 1 && parts[1]) {
    const syllables = parts[1].split("/");

    // Check if there are exactly two syllables
    if (syllables.length === 2) {
      state.word_MSYL_HAS_SAFE_SYLLABLE_COUNT = true;
    }
  }
}

//NOTE: this takes a state which has already been transformed by the heuristicTransformer in studentapp-agent.
//      This transformation cannot be done here because it is in a separate npm package.
//      Remember to run any test data through that transformer before using it in this module
export function prepTransformedStateForRun(transformedState) {
  let passConfigs = getConfigForLocale(transformedState.locale); // assign configs the default setting
  let state = JSON.parse(JSON.stringify(transformedState));
  state.scores = {};
  getReadyScoresForRun(state, passConfigs);
  getFrustrationScoreForRun(state, passConfigs); // Score pass #2
  selectWord(state);
  return state;
}
